import { mediaList, type MediaItem, type MediaType } from "@/mocks/movies";

export type { MediaItem, MediaType };

export const typeMeta: Record<MediaType, { label: string; subtitle: string; description: string }> = {
  movie: {
    label: "电影",
    subtitle: "电影专区",
    description: "海量电影资源，从悬疑到科幻，从剧情到家庭，按题材慢慢挑。",
  },
  tv: {
    label: "电视剧",
    subtitle: "剧集专区",
    description: "治愈日常、悬疑追凶、古装长歌，按集数与地区精准筛选。",
  },
  variety: {
    label: "综艺",
    subtitle: "综艺专区",
    description: "户外真人秀、音乐现场、慢生活纪实，挑一档今晚下饭。",
  },
};

export const isMediaType = (value?: string): value is MediaType =>
  value === "movie" || value === "tv" || value === "variety";

export const getMediaById = (id?: string): MediaItem | undefined =>
  mediaList.find((item) => item.id === id);

export const getByType = (type: MediaType): MediaItem[] =>
  mediaList.filter((item) => item.type === type);

export const regionsOf = (type: MediaType): string[] =>
  Array.from(new Set(getByType(type).map((item) => item.region)));

export const yearsOf = (type: MediaType): number[] =>
  Array.from(new Set(getByType(type).map((item) => item.year))).sort((a, b) => b - a);

export const genresOf = (type: MediaType): string[] =>
  Array.from(new Set(getByType(type).flatMap((item) => item.genres)));

export const typeCounts = {
  movie: getByType("movie").length,
  tv: getByType("tv").length,
  variety: getByType("variety").length,
};

export const formatHeat = (heat: number): string => {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)} 万`;
  return `${heat}`;
};

export const parseList = (value: string | null): string[] =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

export const getRelated = (item: MediaItem, limit = 4): MediaItem[] => {
  const scored = mediaList
    .filter((candidate) => candidate.id !== item.id)
    .map((candidate) => {
      const genreScore = candidate.genres.filter((genre) => item.genres.includes(genre)).length * 3;
      const tagScore = candidate.tags.filter((tag) => item.tags.includes(tag)).length * 2;
      const typeScore = candidate.type === item.type ? 2 : 0;
      const regionScore = candidate.region === item.region ? 1 : 0;
      return { candidate, score: genreScore + tagScore + typeScore + regionScore };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.candidate.heat - a.candidate.heat);

  return scored.slice(0, limit).map((entry) => entry.candidate);
};

export const searchMedia = (keyword: string): MediaItem[] => {
  const word = keyword.trim().toLowerCase();
  if (!word) return [];
  return mediaList.filter((item) =>
    [
      item.title,
      item.originalTitle,
      item.director,
      item.region,
      item.type === "movie" ? "电影" : item.type === "tv" ? "电视剧" : "综艺",
      ...item.genres,
      ...item.tags,
      ...item.cast,
    ]
      .join(" ")
      .toLowerCase()
      .includes(word),
  );
};

export const hotKeywords = ["悬疑", "治愈", "科幻", "古装", "户外", "美食", "2025", "电影"];